import { useMemo } from "react";
import { Flame, CheckCircle2, DollarSign, Percent } from "lucide-react";

import Panel from "../Panel";
import Skeleton from "../Skeleton";
import StatRow, { Stat } from "../StatRow";
import {
  Badge,
  Empty,
  Note,
  PageStack,
  PrimaryText,
  SecondaryText,
  Table,
  TableScroll,
  Td,
  Th,
  Tr,
} from "../ui";
import { PageProps, daysLabel, formatDate } from "./types";
import {
  computeKpis,
  daysLeft,
  formatCurrency,
  isTrialProduct,
  memberDirectory,
  productPrice,
  studioShare,
} from "../../../utils/partnerMetrics";

const OpportunitiesPage = ({ members, products, loading, capabilities }: PageProps) => {
  const trials = useMemo(
    () =>
      memberDirectory(members)
        .filter((m) => m.status === "trial")
        .map((m) => ({ ...m, days: daysLeft(m.expiresAt) }))
        .sort((a, b) => a.days - b.days),
    [members]
  );
  const kpis = useMemo(() => computeKpis(members), [members]);

  const upgradeProduct = products.find(
    (p) => !isTrialProduct(p.name) && /upgraded|premium/i.test(p.name || "")
  );
  const upgradePrice = productPrice(upgradeProduct);
  const checkedIn = trials.filter((t) => t.currentRow.isOnboarded).length;

  const stats: Stat[] = [
    { label: "Active Trials", value: String(trials.length), icon: Flame },
    {
      label: "Checked In",
      value: String(checkedIn),
      icon: CheckCircle2,
      hint: "Have visited this trial",
    },
    {
      label: "Conversion Rate",
      value: `${kpis.conversionRate}%`,
      icon: Percent,
      hint: "Lifetime trial → upgrade",
    },
  ];
  if (capabilities.canViewRevenue && upgradePrice > 0) {
    stats.push({
      label: "Upgrade Potential",
      value: formatCurrency(studioShare(upgradePrice, upgradeProduct?.name) * trials.length),
      icon: DollarSign,
      hint: "Per month if every trial upgrades",
      info: "What you'd earn each month if every active trial member moved to your monthly upgrade plan, using JackedRabbit's standard revenue share. Actual payouts may differ slightly.",
    });
  }

  return (
    <PageStack>
      <StatRow stats={stats} loading={loading} />
      <Panel
        title="Trial Members to Follow Up With"
        subtitle="Soonest trial end first. Members who've already checked in are usually the easiest to convert."
      >
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} $h="36px" $mb="8px" />)
        ) : trials.length === 0 ? (
          <Empty>No trial members are active right now.</Empty>
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>Member</Th>
                  <Th>Phone</Th>
                  <Th>Checked in</Th>
                  <Th>Trial ends</Th>
                  <Th>Time left</Th>
                </tr>
              </thead>
              <tbody>
                {trials.map((t) => (
                  <Tr key={t.key}>
                    <Td>
                      <PrimaryText>
                        {t.firstName} {t.lastName}
                      </PrimaryText>
                      <SecondaryText>{t.email}</SecondaryText>
                    </Td>
                    <Td data-label="Phone" $muted>{t.phone && t.phone !== "null" ? t.phone : "—"}</Td>
                    <Td data-label="Checked in">
                      {t.currentRow.isOnboarded ? (
                        <Badge $tone="green">Checked in</Badge>
                      ) : (
                        <Badge $tone="gray">Not yet</Badge>
                      )}
                    </Td>
                    <Td data-label="Trial ends" $muted>{formatDate(t.expiresAt)}</Td>
                    <Td data-label="Time left">
                      <Badge $tone={t.days <= 3 ? "red" : t.days <= 7 ? "amber" : "gray"}>
                        {daysLabel(t.days)}
                      </Badge>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}
        {upgradePrice > 0 && (
          <Note>
            Your monthly upgrade plan is {formatCurrency(upgradePrice)} for members.
          </Note>
        )}
      </Panel>
    </PageStack>
  );
};

export default OpportunitiesPage;
